import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CheckoutProvider } from '@stripe/react-stripe-js/checkout'
import { loadStripe } from '@stripe/stripe-js'
import { ArrowLeft, Loader2, CheckCircle2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useResolvedTheme } from '@/hooks/use-resolved-theme'
import { buildCheckoutAppearance, CHECKOUT_FONTS } from '@/lib/stripe-appearance'
import type { PlanKeyEnum, BillingIntervalEnum } from '@/gen/cortado/v1/subscriptions_pb'
import {
  clearPendingCheckoutId,
  useCreateCheckoutSession,
  useBillingCheckoutSuccessPoll,
} from '@/stores/billing.store'
import { CheckoutPaymentForm } from './CheckoutPaymentForm'
import type { LiveCheckoutTotals } from './order-summary-model'

type StripePromise = ReturnType<typeof loadStripe>

let cachedStripe: { publishableKey: string; promise: StripePromise } | undefined

function getStripePromise(): StripePromise | null {
  const publishableKey = (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '').trim()
  if (!publishableKey) return null

  if (cachedStripe?.publishableKey !== publishableKey) {
    cachedStripe = {
      publishableKey,
      // TaxIdElement on Checkout Sessions is in public preview and requires
      // the custom Checkout beta. The similarly named elements_tax_id_1 flag
      // applies to Payment Intents and does not add this Checkout SDK method.
      promise: loadStripe(publishableKey, { betas: ['custom_checkout_tax_id_1'] }),
    }
  }
  return cachedStripe.promise
}

function PanelShell({ children }: { children: React.ReactNode }) {
  return <div className="rounded-xl border bg-card p-4">{children}</div>
}

interface InlineCheckoutPanelProps {
  organizationId: string
  planKey: PlanKeyEnum
  billingInterval: BillingIntervalEnum
  checkoutReturnPath?: string
  // Drives the submit label; the trial itself is server-owned.
  trialAvailable?: boolean
  // Called when the customer leaves checkout before completing it.
  onClose?: () => void
  // Called once the post-checkout poll confirms the billing state advanced.
  onSuccess?: () => void
  // Called when Stripe completes payment and backend activation polling begins.
  onActivationStart?: () => void
  onPending?: (checkoutId: string) => void
  // Publishes the live session totals for the order summary.
  onTotalsChange?: (totals: LiveCheckoutTotals | null) => void
}

/**
 * The payment step of the checkout flow: a Checkout Session created by this
 * panel, rendered by our own Stripe Elements form inline beside the persistent
 * order summary. No portal, no overlay, no body scroll lock.
 */
export function InlineCheckoutPanel({
  organizationId,
  planKey,
  billingInterval,
  checkoutReturnPath,
  trialAvailable,
  onClose,
  onSuccess,
  onActivationStart,
  onPending,
  onTotalsChange,
}: InlineCheckoutPanelProps) {
  const { createAsync } = useCreateCheckoutSession()
  const { outcome, startPolling } = useBillingCheckoutSuccessPoll(organizationId)
  const stripePromise = getStripePromise()
  const themeMode = useResolvedTheme()
  const appearance = useMemo(() => buildCheckoutAppearance(themeMode), [themeMode])

  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [createError, setCreateError] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const checkoutIdRef = useRef<string>('')
  const checkoutCompletedRef = useRef(false)
  const mountedRef = useRef(false)
  const selectionGenerationRef = useRef(0)
  const activeSelectionRef = useRef({ organizationId, planKey, billingInterval, retryCount })
  const checkoutRequestRef = useRef<{
    organizationId: string
    planKey: PlanKeyEnum
    billingInterval: BillingIntervalEnum
    retryCount: number
    generation: number
    promise: ReturnType<typeof createAsync>
  } | null>(null)
  const startPollingRef = useRef(startPolling)
  const onCloseRef = useRef(onClose)
  const onTotalsChangeRef = useRef(onTotalsChange)
  onTotalsChangeRef.current = onTotalsChange
  activeSelectionRef.current = { organizationId, planKey, billingInterval, retryCount }
  startPollingRef.current = startPolling
  onCloseRef.current = onClose

  const dismissCheckout = useCallback(() => {
    if (checkoutCompletedRef.current) return
    // An OPEN Checkout is resumable through CreateCheckoutSession's durable
    // intent, but it is not a completed payment awaiting confirmation. Clear
    // any value written by an older client so a reload returns to checkout
    // instead of entering confirmation polling forever.
    clearPendingCheckoutId(organizationId)
    onCloseRef.current?.()
  }, [organizationId])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Create the checkout session once per plan/interval/retry. StrictMode
  // replays reuse the in-flight promise so we do not double-POST Stripe.
  useEffect(() => {
    if (!stripePromise) return

    let effectActive = true
    setClientSecret(null)
    setCreateError(false)
    // The previous session's totals no longer describe anything on screen.
    onTotalsChangeRef.current?.(null)
    checkoutIdRef.current = ''
    checkoutCompletedRef.current = false

    const existingRequest = checkoutRequestRef.current
    const request =
      existingRequest?.organizationId === organizationId &&
      existingRequest.planKey === planKey &&
      existingRequest.billingInterval === billingInterval &&
      existingRequest.retryCount === retryCount
        ? existingRequest
        : (() => {
            const nextRequest = {
              organizationId,
              planKey,
              billingInterval,
              retryCount,
              generation: ++selectionGenerationRef.current,
              promise: createAsync({ organizationId, planKey, billingInterval, returnPath: checkoutReturnPath }),
            }
            checkoutRequestRef.current = nextRequest
            return nextRequest
          })()

    const isCurrentRequest = () => {
      const activeSelection = activeSelectionRef.current
      return (
        effectActive &&
        mountedRef.current &&
        checkoutRequestRef.current === request &&
        selectionGenerationRef.current === request.generation &&
        activeSelection.organizationId === request.organizationId &&
        activeSelection.planKey === request.planKey &&
        activeSelection.billingInterval === request.billingInterval &&
        activeSelection.retryCount === request.retryCount
      )
    }

    request.promise
      .then((response) => {
        if (!isCurrentRequest()) {
          return
        }
        if (!response.clientSecret || !response.checkoutId) {
          setCreateError(true)
          return
        }
        checkoutIdRef.current = response.checkoutId
        setClientSecret(response.clientSecret)
      })
      .catch(() => {
        if (!isCurrentRequest()) {
          return
        }
        setCreateError(true)
      })

    return () => {
      effectActive = false
    }
  }, [createAsync, organizationId, planKey, billingInterval, checkoutReturnPath, retryCount, stripePromise])

  const retryCheckout = useCallback(() => setRetryCount((count) => count + 1), [])

  const handleComplete = useCallback(() => {
    if (checkoutCompletedRef.current || !checkoutIdRef.current) return
    checkoutCompletedRef.current = true
    onActivationStart?.()
    startPollingRef.current(checkoutIdRef.current)
  }, [onActivationStart])

  useEffect(() => {
    if (outcome === 'succeeded') {
      onSuccess?.()
    } else if (outcome === 'stuck' && checkoutIdRef.current) {
      onPending?.(checkoutIdRef.current)
    }
  }, [outcome, onPending, onSuccess])

  const canDismiss = outcome === 'idle' && !checkoutCompletedRef.current

  const backControl =
    onClose && canDismiss ? (
      <Button type="button" variant="ghost" size="sm" onClick={dismissCheckout} className="-ml-2 mb-3">
        <ArrowLeft className="h-4 w-4" />
        Back to plan
      </Button>
    ) : null

  if (!stripePromise) {
    return (
      <PanelShell>
        {backControl}
        <div role="alert" className="flex flex-col items-center gap-3 p-6 text-center">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <p className="text-sm text-destructive">
            Checkout is temporarily unavailable. Please try again later.
          </p>
        </div>
      </PanelShell>
    )
  }

  if (createError) {
    return (
      <PanelShell>
        {backControl}
        <div role="alert" className="flex flex-col items-center gap-3 p-6 text-center">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <p className="text-sm text-destructive">We couldn&apos;t start checkout. Please try again.</p>
          <Button onClick={retryCheckout}>Try again</Button>
        </div>
      </PanelShell>
    )
  }

  if (outcome === 'stuck') return null

  if (outcome === 'succeeded') {
    return (
      <PanelShell>
        <div role="status" className="flex items-center justify-center gap-2 p-6 text-sm">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-status-success-fg" />
          <span>Subscription activated!</span>
        </div>
      </PanelShell>
    )
  }

  if (outcome === 'polling') {
    return (
      <PanelShell>
        <div
          role="status"
          className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Activating your subscription…</span>
        </div>
      </PanelShell>
    )
  }

  if (clientSecret) {
    return (
      <PanelShell>
        {backControl}
        <div className="min-h-[540px]">
          {/* Keyed on the secret because a CheckoutProvider cannot change the
              one it mounted with; a retry issues a new secret and must remount.
              Only the provider is keyed - keying the panel itself would re-run
              the creation effect into a duplicate POST. */}
          <CheckoutProvider
            key={clientSecret}
            stripe={stripePromise}
            options={{
              clientSecret,
              elementsOptions: { appearance, fonts: CHECKOUT_FONTS },
            }}
          >
            <CheckoutPaymentForm
              appearance={appearance}
              trialAvailable={trialAvailable}
              onComplete={handleComplete}
              onSessionExpired={retryCheckout}
              onTotalsChange={onTotalsChange}
            />
          </CheckoutProvider>
        </div>
      </PanelShell>
    )
  }

  return (
    <PanelShell>
      {backControl}
      <div role="status" aria-label="Preparing checkout" className="min-h-[540px] space-y-4">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    </PanelShell>
  )
}
